import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ActionMenu, type ActionMenuItem } from '@fiado/react-kit';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError } from '../../../api/errorMapping';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../../components/ui/dialog';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import { Button } from '../../../components/ui/button';
import { PlatformAdminForm } from './PlatformAdminForm';
import { ManageAdminRolesDialog } from './ManageAdminRolesDialog';
import { AdminAssignmentsList } from './AdminAssignmentsList';
import { useAdminAssignmentLabels } from '../../../hooks/useAdminAssignmentLabels';
import type { PlatformUserListItemLocal } from '../../../api/types/PlatformLocal';

interface Props {
    admin: PlatformUserListItemLocal;
}

/**
 * Menú "⋮" de acciones por fila de admin PLATFORM (`/platform/users`). Entidad DISTINTA a los usuarios
 * de tenant (PlatformUser, endpoints `/platform/users/*`): NO tiene reset de contraseña/MFA ni reenviar
 * invitación (sin endpoint), por eso esas acciones no aparecen. Set: Ver detalle · Gestionar roles ·
 * Editar · Deshabilitar/Habilitar · Eliminar. Usa el <ActionMenu> del kit (cada item gateado con su
 * permiso `platform.*`, scope PLATFORM); el kit oculta el "⋮" si no queda ningún item visible.
 */
export function PlatformAdminRowActions({ admin }: Props) {
    const qc = useQueryClient();
    const sub = admin.cognitoSub;
    const status = admin.status;
    const [detailOpen, setDetailOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [manageRolesOpen, setManageRolesOpen] = useState(false);
    const [confirmDisable, setConfirmDisable] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    // Detalle y roles del admin: se piden solo al abrir «Ver detalle».
    const detailQ = useQuery({
        queryKey: ['platform', 'admins', sub, 'detail'],
        queryFn: () => platformApi.getPlatformAdmin(sub),
        enabled: detailOpen,
    });
    const assignmentsQ = useQuery({
        queryKey: ['platform', 'admins', sub, 'role-assignments'],
        queryFn: () => platformApi.listAdminRoleAssignments(sub),
        enabled: detailOpen,
    });
    const labels = useAdminAssignmentLabels(detailOpen);
    const detail = detailQ.data;

    const invalidate = (): Promise<void> =>
        qc.invalidateQueries({ queryKey: ['platform', 'admins'] });

    const disableMut = useMutation({
        mutationFn: () => platformApi.disablePlatformAdmin(sub),
        onSuccess: async () => {
            toast.success('Usuario deshabilitado');
            setConfirmDisable(false);
            await invalidate();
        },
        onError: handleApiError,
    });
    const enableMut = useMutation({
        mutationFn: () => platformApi.enablePlatformAdmin(sub),
        onSuccess: async () => {
            toast.success('Usuario habilitado');
            await invalidate();
        },
        onError: handleApiError,
    });
    const deleteMut = useMutation({
        mutationFn: () => platformApi.deletePlatformAdmin(sub),
        onSuccess: async () => {
            toast.success('Usuario eliminado');
            setConfirmDelete(false);
            await invalidate();
        },
        onError: handleApiError,
    });

    const items: ActionMenuItem[] = [
        {
            label: 'Ver detalle',
            permission: 'platform.user.view',
            scope: 'PLATFORM',
            onSelect: () => {
                setDetailOpen(true);
            },
        },
        {
            label: 'Gestionar roles',
            permission: 'platform.role.update',
            scope: 'PLATFORM',
            onSelect: () => {
                setManageRolesOpen(true);
            },
        },
        {
            label: 'Editar',
            permission: 'platform.user.update',
            scope: 'PLATFORM',
            onSelect: () => {
                setEditOpen(true);
            },
        },
        ...(status !== 'SUSPENDED'
            ? [
                  {
                      label: 'Deshabilitar',
                      permission: 'platform.user.disable',
                      scope: 'PLATFORM',
                      destructive: true,
                      onSelect: () => {
                          setConfirmDisable(true);
                      },
                  } satisfies ActionMenuItem,
              ]
            : []),
        ...(status === 'SUSPENDED'
            ? [
                  {
                      label: 'Habilitar',
                      permission: 'platform.user.enable',
                      scope: 'PLATFORM',
                      onSelect: () => {
                          enableMut.mutate();
                      },
                  } satisfies ActionMenuItem,
              ]
            : []),
        {
            label: 'Eliminar',
            permission: 'platform.user.delete',
            scope: 'PLATFORM',
            destructive: true,
            onSelect: () => {
                setConfirmDelete(true);
            },
        },
    ];

    return (
        <>
            <ActionMenu items={items} />

            <ManageAdminRolesDialog
                open={manageRolesOpen}
                onOpenChange={setManageRolesOpen}
                cognitoSub={sub}
                adminLabel={admin.displayName || admin.email}
            />

            <ConfirmDialog
                open={confirmDisable}
                onOpenChange={setConfirmDisable}
                title="Deshabilitar usuario"
                description={`${admin.email} no podrá iniciar sesión hasta que lo habilites de nuevo. ¿Continuar?`}
                confirmLabel="Deshabilitar"
                destructive
                onConfirm={() => {
                    disableMut.mutate();
                }}
            />
            <ConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title="Eliminar usuario"
                description={`Se eliminará a ${admin.email}. Esta acción no se puede deshacer. ¿Continuar?`}
                confirmLabel="Eliminar"
                destructive
                loading={deleteMut.isPending}
                onConfirm={() => {
                    deleteMut.mutate();
                }}
            />

            {/* Ver detalle: datos del admin platform. */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Detalle del administrador</DialogTitle>
                        <DialogDescription>{admin.email}</DialogDescription>
                    </DialogHeader>
                    {detailQ.isPending && (
                        <p className="text-sm text-muted-foreground">Cargando…</p>
                    )}
                    {detailQ.isError && (
                        <p className="text-sm text-destructive">
                            No pudimos cargar el detalle del administrador.
                        </p>
                    )}
                    <div className="space-y-1 text-sm">
                        <div>
                            <span className="text-muted-foreground">Nombre: </span>
                            {detail?.displayName ?? admin.displayName}
                        </div>
                        <div>
                            <span className="text-muted-foreground">Email: </span>
                            {detail?.email ?? admin.email}
                        </div>
                        {(detail?.phone ?? undefined) && (
                            <div>
                                <span className="text-muted-foreground">Teléfono: </span>
                                {detail?.phone}
                            </div>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                            <span className="text-muted-foreground">Estado:</span>
                            <StatusBadge status={status} />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <p className="text-sm font-medium">Roles</p>
                        <AdminAssignmentsList
                            items={assignmentsQ.data?.items ?? []}
                            isPending={assignmentsQ.isPending}
                            isError={assignmentsQ.isError}
                            onRetry={() => {
                                void assignmentsQ.refetch();
                            }}
                            labels={labels}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDetailOpen(false);
                            }}
                        >
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Editar: modal plano (mismo patrón que la tabla de crear). */}
            {editOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setEditOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Editar administrador</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setEditOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <PlatformAdminForm
                            admin={admin}
                            onDone={() => {
                                setEditOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
